import { Form, Modal, Select } from "antd"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"

import { type FolderListItem } from "../types/folder.types.ts"

interface MoveFolderModalProps {
  folder: FolderListItem
  folders: FolderListItem[]
  onClose: () => void
  onSubmit: (parentId: string | null) => Promise<void>
}

export function MoveFolderModal({
  folder,
  folders,
  onClose,
  onSubmit
}: MoveFolderModalProps) {
  const { t } = useTranslation()
  const [parentId, setParentId] = useState<string | null>(
    typeof folder.parent === "string" ? folder.parent : null
  )
  const [submitting, setSubmitting] = useState(false)

  // A folder cannot be moved into itself; the backend still guards against
  // moving into one of its own descendants (this list can't know the whole
  // subtree without another round trip).
  const options = folders.filter((candidate) => candidate.id !== folder.id)

  const handleOk = async () => {
    setSubmitting(true)
    try {
      await onSubmit(parentId)
      onClose()
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      open
      title={t("files.moveFolder.title", { name: folder.name })}
      onCancel={onClose}
      onOk={() => void handleOk()}
      confirmLoading={submitting}
      okText={t("files.move.confirm")}
    >
      <Form layout="vertical">
        <Form.Item label={t("files.move.destination")}>
          <Select<string | null>
            value={parentId}
            onChange={setParentId}
            options={[
              { value: null, label: t("files.moveFolder.root") },
              ...options.map((candidate) => ({
                value: candidate.id,
                label: candidate.name
              }))
            ]}
          />
        </Form.Item>
      </Form>
    </Modal>
  )
}
